import {testIdProps} from '@github-ui/test-id-props'
import {Spinner} from '@primer/react'
import {clsx} from 'clsx'
import chunk from 'lodash-es/chunk'
import {useRef} from 'react'

import {getGroupFooterPlaceholder, shouldDisableGroupFooter} from '../../helpers/table-group-utilities'
import {rowIsExpanded} from '../../hooks/tanstack-table/helpers/row-api'
import {getIsTableGrouped} from '../../hooks/tanstack-table/helpers/table-api'
import type {Table} from '../../hooks/tanstack-table/helpers/types'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {LoadingStates} from '../../hooks/use-view-loading-state'
import {pageTypeForGroups, pageTypeForUngroupedItems} from '../../state-providers/memex-items/queries/query-keys'
import {ObserverProvider} from '../board/hooks/use-is-visible'
import {OMNIBAR_SIBLING_PADDING_BOTTOM} from '../omnibar/omnibar'
import {BulkAddItemsProvider} from '../side-panel/bulk-add/bulk-add-items-provider'
import {BulkAddTableSkeleton} from './bulk-add-table-skeleton'
import {ColumnDropZoneSash} from './column-drop-zone-sash'
import {CELL_HEIGHT, TABLE_HEADER_HEIGHT} from './constants'
import {ReorderableRowsContext} from './row-reordering/reorderable-rows'
import {RowReorderSash} from './row-reordering/row-reorder-sash'
import {RowReorderingProvider} from './row-reordering/row-reordering-provider'
import type {TableDataType} from './table-data-type'
import {TableGroup} from './table-group'
import {TableHeader} from './table-header'
import {TablePagination} from './table-pagination'
import {StyledTableRow} from './table-row'
import {TableRowChunk, VIRTUALIZATION_CHUNK_SIZE} from './table-row-chunk'
import styles from './TableBody.module.css'
import type {ItemDataType} from './use-item-data'

type TableBodyProps = {
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  table: Table<TableDataType>
  isOmnibarFixed: boolean
  loadingState: LoadingStates
  hasWritePermissions: boolean
  itemData: ItemDataType
}

export function TableBodyWithObserver({
  scrollRef,
  containerRef,
  table,
  isOmnibarFixed,
  loadingState,

  itemData,
  hasWritePermissions,
}: TableBodyProps) {
  const visibleColumns = table.getVisibleLeafColumns()
  const totalColumnsWidth = table.getTotalSize()
  const isGrouped = getIsTableGrouped(table)
  const topLevelRows = table.getPreExpandedRowModel().rows
  const {memex_small_viewport_a11y} = useEnabledFeatures()
  const headerRef = useRef(null)
  return (
    <ObserverProvider rootRef={scrollRef} sizeEstimate={CELL_HEIGHT * VIRTUALIZATION_CHUNK_SIZE}>
      <div
        className={clsx(styles.tableScrollContainer, styles.Box)}
        // Passing feature flag as data attribute so it can be accessed in CSS
        data-memex-small-viewport-a11y={memex_small_viewport_a11y}
        {...testIdProps('table-scroll-container')}
        ref={scrollRef}
      >
        <div
          style={{
            width: totalColumnsWidth,
            // Leave enough space at the bottom for the omnibar not to obscure the last row.
            paddingBottom: isOmnibarFixed ? OMNIBAR_SIBLING_PADDING_BOTTOM : 'unset',
            '--table-cell-height': `${CELL_HEIGHT}px`,
          }}
          data-memex-small-viewport-a11y={memex_small_viewport_a11y}
          className={styles.tableScrollContainerInner}
        >
          <TableHeader
            headerGroups={table.getHeaderGroups()}
            height={TABLE_HEADER_HEIGHT}
            scrollRef={scrollRef}
            tableWidth={totalColumnsWidth}
            ref={headerRef}
          />
          <ColumnDropZoneSash visibleColumns={visibleColumns} height={TABLE_HEADER_HEIGHT} sticky />
          <div ref={containerRef}>
            <RowReorderingProvider>
              <BulkAddItemsProvider>
                {loadingState === LoadingStates.loading ? (
                  <StyledTableRow className={styles.StyledTableRow} {...testIdProps('view-loading-indicator')}>
                    <Spinner size="small" />
                    Loading...
                  </StyledTableRow>
                ) : loadingState === LoadingStates.missing ? null : isGrouped ? (
                  topLevelRows.map(group => {
                    return (
                      <TableGroup
                        key={group.id}
                        groupId={group.id}
                        rows={group.subRows}
                        metadata={{
                          value: group.groupedValue,
                          sourceObject: group.groupedSourceObject,
                        }}
                        headerRef={headerRef}
                        totalCount={group.totalCount}
                        itemData={itemData}
                        isCollapsed={!rowIsExpanded(group)}
                        scrollRef={scrollRef}
                        shouldDisableFooter={shouldDisableGroupFooter(group.groupedSourceObject)}
                        footerPlaceholder={getGroupFooterPlaceholder(group)}
                        isEditable={hasWritePermissions}
                      />
                    )
                  })
                ) : (
                  <ReorderableRowsContext siblingRows={topLevelRows}>
                    {chunk(topLevelRows, VIRTUALIZATION_CHUNK_SIZE).map((rowChunk, index) => (
                      <TableRowChunk
                        // eslint-disable-next-line @eslint-react/no-array-index-key
                        key={index}
                        rows={rowChunk}
                        itemData={itemData}
                      />
                    ))}
                  </ReorderableRowsContext>
                )}
                <RowReorderSash rows={topLevelRows} isGrouped={isGrouped} scrollRef={scrollRef} />
              </BulkAddItemsProvider>
            </RowReorderingProvider>
            <TablePagination pageType={isGrouped ? pageTypeForGroups : pageTypeForUngroupedItems} />
            <BulkAddTableSkeleton />
          </div>
          <ColumnDropZoneSash visibleColumns={visibleColumns} />
        </div>
      </div>
    </ObserverProvider>
  )
}

try{ TableBodyWithObserver.displayName ||= 'TableBodyWithObserver' } catch {}